import React, { useEffect, useRef, useState } from 'react';
import { downloadHiredEquipmentPdf } from '../utils/downloadHePdf';
import { describeDecisionError } from '@/utils/loadError';
import { useSearchParams, Link } from 'react-router-dom';
import { Alert, Badge, Button, Spinner } from '@/components/ui';
import { describeActionError } from '@/utils/loadError';
import { Form, formService, PaginatedFormsResponse, FormQueryParams, FormFilterOptions, FormFilterCounts, DuplicateFormWarning } from '../../../services/formService';

import FormCard from './FormCard';
import { Task } from '../../../types/Task';
import { taskService } from '../../../services/taskService';
import effortService from '../../../services/effortService';
import type {
  FormLabourOvertimeAllocationRow,
  PersistedLabourEffort
} from '../../../utils/labourOvertimeDisplay';
import FormFilters, { FormAdvancedFilters } from './FormFilters';
import {
  APPROVAL_FORM_TYPES,
  type ApprovalFormType,
  type ApprovalStatusFilter,
} from '../config/approvalFormTypes';
import ServerPagination from '../../../components/ServerPagination';
import { useAuth } from '../../../contexts/AuthContext';
import Toast, { ToastType } from '../../../components/Toast';
import DuplicateFormWarningModal from '../../../components/DuplicateFormWarningModal';
import '../styles/FormList.css';
import { DECISION_FEEDBACK_MS, type DecisionResult } from '@/components/ui';
import { confirmAmountChanged, readAmountChange } from '../../hired-equipment/utils/amountChangedDialog';
import { notifyApprovalsChanged } from '../../../components/awaitingApprovalsCount';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import { statusTabForFormStatus } from '../submissionsPaths';

interface FormListProps {
  formType: ApprovalFormType;
  onFormTypeChange?: (formType: ApprovalFormType) => void;
  hasWorkId?: boolean;
}

/** sessionStorage page key for the remembered list state (V2-024, FLT-09). */
const SUBMISSIONS_PAGE_KEY = 'submissions';

// Add pagination constant - removed ITEMS_PER_PAGE since we'll use server pagination

const VALID_STATUSES = ['all', 'pending', 'need revisions', 'approved', 'declined'] as const;

const STATUS_FILTER_BUTTONS: { value: ApprovalStatusFilter; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'need revisions', label: 'Need Revisions' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
];

const EMPTY_ADVANCED_FILTERS: FormAdvancedFilters = {
  employeeNames: [],
  subcontractorNames: [],
  startDate: null,
  endDate: null,
  foremanAreaIds: [],
  serviceAreaIds: [],
};

/**
 * URL params carry multi-value filters as a comma separated list (same convention as the
 * server and the Work IDs page). A single bare value still works.
 */
function csvToList(raw: string | null | undefined): string[] {
  return (raw ?? '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value.length > 0);
}

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

function getInitialFiltersFromSearchParams(searchParams: URLSearchParams) {
  const status = searchParams.get('status');
  const foremanArea = searchParams.get('foremanArea') ?? searchParams.get('foremanAreaId');
  const serviceAreaId = searchParams.get('serviceAreaId');
  return {
    statusFilter: (status && VALID_STATUSES.includes(status as typeof VALID_STATUSES[number])) ? status : 'pending',
    foremanAreaIds: csvToList(foremanArea),
    serviceAreaIds: csvToList(serviceAreaId),
  };
}

/**
 * V3-050: hired equipment forms carry their tasks' number and name (`heTasks`), so a viewer whose
 * task list call returns less (no task permission, or out of area) sees the same label as anyone
 * else – never the bare task id.
 */
function withHeTaskRefs(tasks: Task[], forms: Form[]): Task[] {
  const known = new Set(tasks.map((t) => Number(t.id)));
  const extra: Task[] = [];
  for (const form of forms) {
    for (const ref of (form as Form & { heTasks?: Array<{ id: number; taskNumber: string | null; description: string | null; name: string | null }> }).heTasks ?? []) {
      if (known.has(ref.id)) continue;
      known.add(ref.id);
      extra.push({ id: ref.id, taskNumber: ref.taskNumber ?? undefined, description: ref.description ?? ref.name ?? '', name: ref.name ?? ref.description ?? '' } as unknown as Task);
    }
  }
  return extra.length ? [...tasks, ...extra] : tasks;
}

/** V3-017: what the approve response says about the vendor email (HE only). */
interface VendorEmailOutcome {
  requested: boolean;
  sent: boolean;
  reason: string | null;
}

/** The not-sent result stays long enough to use its Download PDF action. */
const VENDOR_EMAIL_NOT_SENT_MS = 15000;

function vendorEmailNotSentReason(reason: string | null | undefined): string {
  switch (reason) {
    case 'email_not_configured':
      return 'email is off on this environment';
    case 'no_vendor_email':
      return 'the vendor has no contact email on file';
    case 'no_vendor_on_form':
    case 'vendor_not_found':
      return 'no vendor record found';
    case 'pdf_failed':
      return "the PDF couldn't be generated";
    default:
      return 'the send failed';
  }
}

const FormList: React.FC<FormListProps> = ({ formType, onFormTypeChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useAuth();
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // V2-024 / FLT-09: the status tab, card filters, search and page are kept for the sign-in.
  // A link's own ?status= / area params still win: the URL sync effects below apply them.
  const [statusFilter, setStatusFilter] = usePersistedListState<string>(
    SUBMISSIONS_PAGE_KEY,
    'status',
    getInitialFiltersFromSearchParams(searchParams).statusFilter
  );
  const [isLoading, setIsLoading] = useState(false);
  const [availableTasks, setAvailableTasks] = useState<Task[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [persistedLabourByFormId, setPersistedLabourByFormId] = useState<
    Record<string, PersistedLabourEffort[]>
  >({});
  const [labourOtAllocationsByFormId, setLabourOtAllocationsByFormId] = useState<
    Record<string, FormLabourOvertimeAllocationRow[]>
  >({});
  const [advancedFilters, setAdvancedFilters] = usePersistedListState<FormAdvancedFilters>(
    SUBMISSIONS_PAGE_KEY,
    'filters',
    EMPTY_ADVANCED_FILTERS
  );
  const [searchTerm, setSearchTerm] = usePersistedListState(SUBMISSIONS_PAGE_KEY, 'search', '');
  // V2-038: "Submitted by Me" – kept for the sign-in with the other filters.
  const [submittedByMe, setSubmittedByMe] = usePersistedListState(SUBMISSIONS_PAGE_KEY, 'mine', false);
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [savedPage, setSavedPage] = usePersistedListState(SUBMISSIONS_PAGE_KEY, 'page', { page: 1, limit: 20 });

  // Add pagination state
  const [pagination, setPagination] = useState({
    page: savedPage.page,
    limit: savedPage.limit,
    totalCount: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });

  // Add filter options state for server-side filtering
  /** V3-036: the filter options (area names) have arrived, or failed – see waitingForAreaNames. */
  const [filterOptionsLoaded, setFilterOptionsLoaded] = useState(false);
  const [filterOptions, setFilterOptions] = useState<FormFilterOptions>({
    serviceAreas: [],
    foremanAreas: [],
    statuses: [],
    employeeNames: [],
    subcontractorNames: []
  });

  // Add filter counts state for badges
  const [filterCounts, setFilterCounts] = useState<FormFilterCounts>({
    statusCounts: {
      all: 0,
      pending: 0,
      approved: 0,
      'need revisions': 0,
      declined: 0,
      'revisions completed': 0
    },
    formTypeCounts: {
      all: 0,
      'Daily Log': 0,
      'Hired Equipment': 0
    }
  });

  // Toast state
  const [toast, setToast] = useState<{
    show: boolean;
    message: string;
    type: ToastType;
  }>({
    show: false,
    message: '',
    type: 'info'
  });

  // Duplicate warning modal state
  const [duplicateWarningModal, setDuplicateWarningModal] = useState<{
    isOpen: boolean;
    duplicateWarning: DuplicateFormWarning | null;
    pendingFormId: string | null;
    /** HE-F21: the vendor-PDF answer must survive the duplicate detour and the retry. */
    pendingOptions?: { emailVendorPdf?: boolean; vendorEmail?: { to: string; subject: string; bodyText: string }; displayedTotal?: number };
  }>({
    isOpen: false,
    duplicateWarning: null,
    pendingFormId: null
  });

  // Track forms being processed (formId -> action type)
  const [processingForms, setProcessingForms] = useState<Map<string, string>>(new Map());
  const [fadingForms, setFadingForms] = useState<Map<string, string>>(new Map());
  // V2-013 / STATE-09: each decision's result stays on its card for about 5 s, then the card
  // leaves; a failure stays on the card with Retry.
  const [decisionResults, setDecisionResults] = useState<Map<string, DecisionResult>>(new Map());
  const decisionTimersRef = useRef<Map<string, { timer: ReturnType<typeof setTimeout>; mergeTaskIds: number[] }>>(new Map());

  const clearProcessing = (formId: string) =>
    setProcessingForms(prev => {
      const next = new Map(prev);
      next.delete(formId);
      return next;
    });

  const setDecisionResult = (formId: string, result: DecisionResult | null) =>
    setDecisionResults(prev => {
      const next = new Map(prev);
      if (result) next.set(formId, result);
      else next.delete(formId);
      return next;
    });

  /** The finished card leaves the list; the page refreshes in place. */
  const dismissDecision = (formId: string) => {
    const pending = decisionTimersRef.current.get(formId);
    if (pending) clearTimeout(pending.timer);
    decisionTimersRef.current.delete(formId);
    setDecisionResult(formId, null);
    setForms(prev => prev.filter(f => f.id !== formId));
    void fetchForms(false, undefined, pending?.mergeTaskIds ?? [], undefined, true);
  };

  /** Acting on another card first: the finished ones leave at once. */
  const startDecision = (formId: string, kind: string) => {
    for (const id of Array.from(decisionTimersRef.current.keys())) {
      if (id !== formId) dismissDecision(id);
    }
    setDecisionResult(formId, null);
    setProcessingForms(prev => new Map(prev).set(formId, kind));
  };

  const finishDecision = (
    formId: string,
    message: string,
    mergeTaskIds: number[] = [],
    status?: string,
    extra?: { detail?: React.ReactNode; holdMs?: number },
  ) => {
    clearProcessing(formId);
    // V3-015: the card shows its new status pill while the message shows.
    setDecisionResult(formId, { tone: 'success', message, status, detail: extra?.detail });
    // Counts and the navbar badge update at once, not when the card leaves.
    void fetchFilterCounts();
    notifyApprovalsChanged();
    const timer = setTimeout(() => dismissDecision(formId), extra?.holdMs ?? DECISION_FEEDBACK_MS);
    decisionTimersRef.current.set(formId, { timer, mergeTaskIds });
  };

  const failDecision = (formId: string, message: string, onRetry?: () => void) => {
    clearProcessing(formId);
    setDecisionResult(formId, { tone: 'error', message, onRetry });
  };

  useEffect(() => {
    const timers = decisionTimersRef.current;
    return () => timers.forEach(({ timer }) => clearTimeout(timer));
  }, []);

  // Form to expand when opened via deep link (e.g. /approvals?formId=123 from Task efforts)
  const formIdFromUrl = searchParams.get('formId');
  const [expandFormId, setExpandFormId] = useState<string | null>(() => formIdFromUrl);

  const filterCountsRequestIdRef = useRef(0);
  const formsFetchRequestIdRef = useRef(0);

  const handleSubmittedByMeChange = (mine: boolean) => {
    setSubmittedByMe(mine);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const submittedByMeSwitch = (id: string) => (
    <label htmlFor={id} className="tw-flex tw-min-h-[2.25rem] tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-content">
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={submittedByMe}
        onChange={(e) => handleSubmittedByMeChange(e.target.checked)}
        className="tw-h-4 tw-w-4 tw-rounded tw-border-line-input tw-text-primary"
      />
      Submitted by Me
    </label>
  );

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (status === 'pending') {
          next.delete('status');
        } else {
          next.set('status', status);
        }
        return next;
      },
      { replace: true },
    );
  };

  // Sync URL params (status + foreman area + service area) when they change (e.g. back/forward or link).
  // Initial values are set from URL in useState above so the first fetch already has correct filters.
  useEffect(() => {
    const status = searchParams.get('status');
    const foremanArea = searchParams.get('foremanArea');
    const serviceAreaId = searchParams.get('serviceAreaId');
    const formId = searchParams.get('formId');
    if (status && VALID_STATUSES.includes(status as typeof VALID_STATUSES[number])) {
      setStatusFilter(status);
    }
    if (foremanArea !== null && foremanArea !== undefined || serviceAreaId !== null && serviceAreaId !== undefined) {
      setAdvancedFilters((prev) => {
        const nextForemanAreaIds = foremanArea !== null && foremanArea !== undefined
          ? csvToList(foremanArea)
          : prev.foremanAreaIds;
        const nextServiceAreaIds = serviceAreaId !== null && serviceAreaId !== undefined
          ? csvToList(serviceAreaId)
          : prev.serviceAreaIds;
        // Keep the same object identity when nothing changed: advancedFilters is a fetch dependency.
        if (sameList(nextForemanAreaIds, prev.foremanAreaIds) && sameList(nextServiceAreaIds, prev.serviceAreaIds)) {
          return prev;
        }
        return { ...prev, foremanAreaIds: nextForemanAreaIds, serviceAreaIds: nextServiceAreaIds };
      });
    }
    if (formId !== null && formId !== undefined) {
      setExpandFormId(formId);
    }
  }, [searchParams]);

  const getFormCountByStatus = (status: string) => {
    return filterCounts.statusCounts[status as keyof typeof filterCounts.statusCounts] || 0;
  };

  /**
   * Multi-value filters travel as comma separated lists (`foremanArea=12,34`).
   * Empty selections omit the param entirely, which the server reads as "no filter".
   * Service areas are still sent by NAME (server matches on name), ids are resolved here.
   *
   * Employee / subcontractor names are FREE TEXT and can legitimately contain commas
   * ("Acme Ltd., Inc"), so each element is percent-encoded before joining (commas -> %2C)
   * and the server decodes after splitting. Same comma-safe token convention as the
   * Work IDs work-source filter.
   */
  const buildAdvancedFilterQueryParams = (): Pick<
    FormQueryParams,
    'serviceArea' | 'foremanArea' | 'employeeName' | 'subcontractorName'
  > => {
    const serviceArea = advancedFilters.serviceAreaIds
      .map((id) => filterOptions.serviceAreas.find((sa) => sa.id.toString() === id)?.name)
      .filter((name): name is string => Boolean(name))
      .join(',');

    const namesToCsv = (names: string[]): string => names.map(encodeURIComponent).join(',');

    return {
      ...(serviceArea && { serviceArea }),
      ...(advancedFilters.foremanAreaIds.length > 0 && {
        foremanArea: advancedFilters.foremanAreaIds.join(',')
      }),
      ...(advancedFilters.employeeNames.length > 0 && {
        employeeName: namesToCsv(advancedFilters.employeeNames)
      }),
      ...(advancedFilters.subcontractorNames.length > 0 && {
        subcontractorName: namesToCsv(advancedFilters.subcontractorNames)
      })
    };
  };

  const buildCountsQueryParams = (): Omit<FormQueryParams, 'page' | 'limit' | 'status'> => ({
    ...(formType !== 'all' && { formType }),
    ...(submittedByMe && { submittedBy: 'me' as const }),
    ...buildAdvancedFilterQueryParams(),
    ...(searchTerm && { search: searchTerm }),
    ...(advancedFilters.startDate && { dateFrom: advancedFilters.startDate }),
    ...(advancedFilters.endDate && { dateTo: advancedFilters.endDate }),
  });

  // Updated fetch functions
  const fetchForms = async (
    resetPage: boolean = false,
    signal?: AbortSignal,
    /** Extra task IDs to load (e.g. from just-approved form) so efforts/OT merge even if list page shifts. */
    mergeTaskIds: number[] = [],
    requestId?: number,
    /** V2-013: refresh in place after a decision – no full-list spinner. */
    silent: boolean = false,
  ) => {
    const isLatest = () => requestId == null || requestId === formsFetchRequestIdRef.current;
    try {
      if (!silent) setLoading(true);
      setError(null);

      // Use current pagination page or reset to 1
      const currentPage = resetPage ? 1 : pagination.page;

      // Build query parameters
      const queryParams: FormQueryParams = {
        page: currentPage,
        limit: pagination.limit,
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(formType !== 'all' && { formType: formType }),
        ...(submittedByMe && { submittedBy: 'me' as const }),
        ...buildAdvancedFilterQueryParams(),
        ...(searchTerm && { search: searchTerm }),
        ...(advancedFilters.startDate && { dateFrom: advancedFilters.startDate }),
        ...(advancedFilters.endDate && { dateTo: advancedFilters.endDate })
      };

      console.log('Sending query params:', queryParams); // Debug log

      const response = await formService.getFormsPaginated(queryParams, signal);

      if (signal?.aborted || !isLatest()) return;

      // No need for client-side filtering anymore since we're filtering on the server
      setForms(response.data);
      setPagination({
        page: response.pagination.page,
        limit: response.pagination.limit,
        totalCount: response.pagination.totalCount,
        totalPages: response.pagination.totalPages,
        hasNextPage: response.pagination.hasNextPage,
        hasPreviousPage: response.pagination.hasPreviousPage
      });

      if (resetPage) {
        setPagination(prev => ({ ...prev, page: 1 }));
      }

      await fetchTasksForForms(response.data, mergeTaskIds);
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') return;
      if (!isLatest()) return;
      console.error('Error loading forms:', error);
      setError('Failed to load forms. Please try again later.');
      setForms([]);
      setPagination(prev => ({ ...prev, totalCount: 0, totalPages: 0 }));
    } finally {
      if (isLatest()) {
        setLoading(false);
      }
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const options = await formService.getFilterOptions();
      setFilterOptions(options);
      console.log('Fetched filter options:', options); // Debug log
    } catch (error) {
      console.error('Error fetching filter options:', error);
    } finally {
      setFilterOptionsLoaded(true);
    }
  };

  // Helper function to extract task IDs from forms
  const extractTaskIdsFromForms = (forms: Form[]): number[] => {
    const taskIds = new Set<number>();
    
    forms.forEach(form => {
      form.fields.forEach(field => {
        // Look for Task fields within WorkCompleted sections
        if (field.path.includes('WorkCompleted') && field.field === 'Task' && field.path.endsWith('.Task')) {
          const taskId = parseInt(field.value);
          if (!isNaN(taskId) && taskId > 0) {
            taskIds.add(taskId);
            console.log(`Found task ID from Task field: ${taskId} in form ${form.id}`);
          }
        }
        
        // Also check WorkCompletedID fields
        if (field.path.includes('WorkCompleted') && field.field === 'WorkCompletedID') {
          const taskId = parseInt(field.value);
          if (!isNaN(taskId) && taskId > 0) {
            taskIds.add(taskId);
            console.log(`Found task ID from WorkCompletedID field: ${taskId} in form ${form.id}`);
          }
        }

        // TaskId (numeric id) — matches FormSummaryView; needed so /light/by-ids loads task.efforts for OT merge
        if (
          field.path.includes('WorkCompleted') &&
          (field.field === 'TaskId' || /\.TaskId$/i.test(field.path))
        ) {
          const taskId = parseInt(String(field.value), 10);
          if (!isNaN(taskId) && taskId > 0) {
            taskIds.add(taskId);
            console.log(`Found task ID from TaskId field: ${taskId} in form ${form.id}`);
          }
        }
      });
    });
    
    const result = Array.from(taskIds);
    console.log(`Extracted ${result.length} unique task IDs from ${forms.length} forms:`, result);
    return result;
  };

  /** Batched labour rows (clone tasks + DRM OT) for submission summary merge. */
  const fetchPersistedLabourForForms = async (forms: Form[]) => {
    const ids = Array.from(new Set(forms.map((f) => String(f.id)).filter(Boolean))).slice(0, 100);
    if (ids.length === 0) {
      setPersistedLabourByFormId({});
      setLabourOtAllocationsByFormId({});
      return;
    }
    try {
      const [rows, allocRows] = await Promise.all([
        effortService.getLabourEffortsByFormIds(ids),
        effortService.getLabourOvertimeAllocationsByFormIds(ids)
      ]);
      const byForm: Record<string, PersistedLabourEffort[]> = {};
      for (const row of rows) {
        const fid = String(row.formId ?? '');
        if (!byForm[fid]) byForm[fid] = [];
        byForm[fid].push(row);
      }
      const byFormAlloc: Record<string, FormLabourOvertimeAllocationRow[]> = {};
      for (const row of allocRows) {
        const fid = String(row.formId ?? '');
        if (!byFormAlloc[fid]) byFormAlloc[fid] = [];
        byFormAlloc[fid].push(row);
      }
      setPersistedLabourByFormId(byForm);
      setLabourOtAllocationsByFormId(byFormAlloc);
    } catch (e) {
      console.error('Error fetching labour efforts for forms:', e);
      setPersistedLabourByFormId({});
      setLabourOtAllocationsByFormId({});
    }
  };

  /**
   * After labour OT edits: refetch allocation rows + labour efforts, and refresh light tasks
   * so `task.efforts` matches DB (Print PDF uses `availableTasks`, not the merged summary state).
   */
  const refreshLabourAndLightTasksForForms = async (forms: Form[]) => {
    const taskIdList = extractTaskIdsFromForms(forms);
    try {
      await Promise.all([
        fetchPersistedLabourForForms(forms),
        (async () => {
          if (taskIdList.length === 0) return;
          const tasks = await taskService.getLightTasksByIds(taskIdList).catch(() => [] as Task[]);
          setAvailableTasks(withHeTaskRefs(tasks, forms));
        })()
      ]);
    } catch (e) {
      console.error('Error refreshing labour/tasks after OT update:', e);
    }
  };

  // Fetch tasks referenced on the page + persisted labour by form (for OT on clone tasks).
  const fetchTasksForForms = async (forms: Form[], mergeTaskIds: number[] = []) => {
    try {
      setTasksLoading(true);
      const labourPromise = fetchPersistedLabourForForms(forms);

      const taskIds = new Set<number>(extractTaskIdsFromForms(forms));
      for (const id of mergeTaskIds) {
        if (typeof id === 'number' && !isNaN(id) && id > 0) {
          taskIds.add(id);
        }
      }
      const taskIdList = Array.from(taskIds);
      console.log('Extracted task IDs from forms (+ merge):', taskIdList);

      let tasksPromise: Promise<void> = Promise.resolve();
      if (taskIdList.length === 0) {
        setAvailableTasks([]);
      } else {
        tasksPromise = taskService
          .getLightTasksByIds(taskIdList)
          .then((tasks) => setAvailableTasks(withHeTaskRefs(tasks, forms)))
          .catch((err) => {
            // A viewer without task access (e.g. Sourcing Manager) still sees the HE task labels.
            console.error('Error fetching tasks for forms:', err);
            setAvailableTasks(withHeTaskRefs([], forms));
          });
      }

      await Promise.all([tasksPromise, labourPromise]);
    } catch (error) {
      console.error('Error fetching tasks/labour for forms:', error);
      setAvailableTasks([]);
    } finally {
      setTasksLoading(false);
    }
  };

  // Add pagination handlers
  useEffect(() => {
    setSavedPage({ page: pagination.page, limit: pagination.limit });
  }, [pagination.page, pagination.limit, setSavedPage]);

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // Add a small delay to prevent immediate re-render from interfering with dropdown
    setTimeout(() => {
      setPagination(prev => ({ 
        ...prev, 
        limit: newPageSize, 
        page: 1 // Reset to first page when changing page size
      }));
    }, 100);
  };

  // Request id so we only apply the latest filter-counts response (avoid stale overwrites)
  const fetchFilterCounts = async () => {
    const requestId = ++filterCountsRequestIdRef.current;
    try {
      const counts = await formService.getFilterCounts(buildCountsQueryParams());
      // Only update state if this is still the latest request (ignore stale responses)
      if (requestId === filterCountsRequestIdRef.current) {
        setFilterCounts(counts);
      }
    } catch (error) {
      if (requestId === filterCountsRequestIdRef.current) {
        console.error('Error fetching filter counts:', error);
      }
    }
  };

  // M18: filter options only change the request when a service-area filter is selected (ids are
  // sent as names). Depending on the whole options object fetched the list and the counts twice on
  // every page load – once before the options arrived and once after.
  const selectedServiceAreaNamesKey = advancedFilters.serviceAreaIds
    .map((id) => filterOptions.serviceAreas.find((sa) => sa.id.toString() === id)?.name ?? `#${id}`)
    .join('|');
  // V3-036: with a remembered Service Area filter, the request needs the area NAMES, which come
  // with the filter options. Waiting for them sends /api/forms once, with the filter, instead of
  // once without it (wrong rows) and again when the options arrive.
  const waitingForAreaNames = advancedFilters.serviceAreaIds.length > 0 && !filterOptionsLoaded;

  // Single effect to fetch forms when any relevant parameter changes.
  // Skip when formId is in URL (single-form view) so we don't load the full list.
  const fetchAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (formIdFromUrl) return; // Single-form view: handled by separate effect
    if (waitingForAreaNames) return;
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const requestId = ++formsFetchRequestIdRef.current;
    fetchForms(false, controller.signal, [], requestId);
    return () => {
      controller.abort();
      fetchAbortRef.current = null;
    };
  }, [pagination.page, pagination.limit, statusFilter, formType, submittedByMe, advancedFilters, searchTerm, selectedServiceAreaNamesKey, formIdFromUrl, waitingForAreaNames]);

  // Status pill counts follow the same filters as the list (type tab, card filters, search).
  useEffect(() => {
    if (formIdFromUrl) return;
    if (waitingForAreaNames) return;
    fetchFilterCounts();
  }, [formType, submittedByMe, advancedFilters, searchTerm, selectedServiceAreaNamesKey, formIdFromUrl, waitingForAreaNames]);

  // Initial data loading
  useEffect(() => {
    fetchFilterOptions();
  }, []);

  // Pre-populate filters from URL (e.g. from homepage widget link) so dropdowns show selected items
  useEffect(() => {
    const foremanAreaId = searchParams.get('foremanAreaId');
    const serviceAreaId = searchParams.get('serviceAreaId');
    if (foremanAreaId != null || serviceAreaId != null) {
      setAdvancedFilters((prev) => {
        const nextForemanAreaIds = foremanAreaId != null && foremanAreaId !== ''
          ? csvToList(foremanAreaId)
          : prev.foremanAreaIds;
        const nextServiceAreaIds = serviceAreaId != null && serviceAreaId !== ''
          ? csvToList(serviceAreaId)
          : prev.serviceAreaIds;
        if (sameList(nextForemanAreaIds, prev.foremanAreaIds) && sameList(nextServiceAreaIds, prev.serviceAreaIds)) {
          return prev;
        }
        return { ...prev, foremanAreaIds: nextForemanAreaIds, serviceAreaIds: nextServiceAreaIds };
      });
    }
  }, [searchParams]);

  // V3-072: a deep link to a record that doesn't exist says so, with a way back.
  const [missingFormId, setMissingFormId] = useState<string | null>(null);
  const missingReturnTo = (() => {
    const back = searchParams.get('returnTo');
    // Only a path inside WRAP's Equipment pages, never an outside address.
    return back && /^\/equipment\/\d+(\?[\w=&-]*)?$/.test(back) ? back : null;
  })();
  const missingWhat = searchParams.get('kind') === 'daily-log' ? 'daily log' : 'submission';

  // When opened via deep link (?formId=), show only that single form (no list, no extra timecards)
  useEffect(() => {
    if (!formIdFromUrl) return;
    setLoading(true);
    setForms([]);
    setMissingFormId(null);
    let cancelled = false;
    formService.getForm(formIdFromUrl)
      .then((form) => {
        if (cancelled) return;
        setForms([form]);
        // V2-049: the record opens on the tab matching its status, not on the last tab used.
        setStatusFilter(statusTabForFormStatus(form.status));
        setExpandFormId(formIdFromUrl);
        fetchTasksForForms([form]);
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn('Could not load form by id for deep link:', formIdFromUrl, err);
          setForms([]);
          setMissingFormId(formIdFromUrl);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [formIdFromUrl]);

  // Helper function to get date value from form field
  const getDateFromForm = (form: Form, fieldName: string): string | null => {
    const dateField = form.fields.find(
      field => field.field === fieldName || 
              field.question.toLowerCase().includes(fieldName.toLowerCase())
    );
    
    if (dateField?.value) {
      return dateField.value;
    }
    
    return null;
  };

  // Helper function to get employee name from form
  const getEmployeeNameFromForm = (form: Form): string | null => {
    const employeeField = form.fields.find(
      field => field.field === 'EmployeeName' || 
              field.question.toLowerCase().includes('employee name')
    );
    
    return employeeField?.value || null;
  };

  // Manual search (Enter or the clear button), no automatic search on typing: the server search
  // covers ticket numbers and the whole form, so it runs when the user asks (V2-077).
  const handleSearch = (value: string) => {
    setSearchTerm(value.trim());
    setPagination(prev => ({ ...prev, page: 1 }));
  };



  const handleApprove = async (
    formId: string,
    confirmDuplicate: boolean = false,
    options?: {
      emailVendorPdf?: boolean;
      vendorEmail?: { to: string; subject: string; bodyText: string };
      displayedTotal?: number;
    }
  ): Promise<void> => {
    // Find the form to check the employee name
    const form = forms.find(f => f.id === formId);
    if (form && currentUser) {
      const employeeName = getEmployeeNameFromForm(form);
      const currentUserFullName = `${currentUser.name} ${currentUser.lastName}`;
      if (employeeName === currentUserFullName) {
        setToast({
          show: true,
          message: "Can't approve own timecard",
          type: 'warning'
        });
        return;
      }
    }

    // Mark form as processing immediately
    startDecision(formId, 'approve');

    try {
      const approvedForm = await formService.approveForm(formId, confirmDuplicate, options);
      const mergeTaskIds = extractTaskIdsFromForms([approvedForm]);
      
      
      // V3-017: Approve and send – say so when the vendor email wasn't sent, and offer the PDF.
      const emailOutcome = (approvedForm as { vendorEmail?: VendorEmailOutcome } | undefined)?.vendorEmail;
      if (options?.emailVendorPdf && emailOutcome?.requested && !emailOutcome.sent) {
        const pdfForm = approvedForm?.fields?.length ? approvedForm : form;
        finishDecision(formId, `Approved – vendor email not sent (${vendorEmailNotSentReason(emailOutcome.reason)}).`, mergeTaskIds, 'approved', {
          holdMs: VENDOR_EMAIL_NOT_SENT_MS,
          detail: pdfForm ? (
            <button
              type="button"
              className="tw-border-0 tw-bg-transparent tw-p-0 tw-font-semibold tw-text-primary tw-underline"
              onClick={() => {
                void downloadHiredEquipmentPdf(pdfForm, availableTasks).catch(() =>
                  setToast({ show: true, message: "Couldn't generate the PDF. Try again.", type: 'error' })
                );
              }}
            >
              Download PDF
            </button>
          ) : undefined,
        });
      } else {
        finishDecision(formId, 'Approved – moving to Approved', mergeTaskIds, 'approved');
      }
    } catch (error: any) {
      console.error('Error approving form:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      // Handle duplicate Daily Log warning
      if (error.response?.status === 409 && error.response?.data?.error === 'DUPLICATE_DAILY_LOG_WARNING') {
        const duplicateWarning: DuplicateFormWarning = error.response.data;
        
        // Show the duplicate warning modal instead of window.confirm
        setDuplicateWarningModal({
          isOpen: true,
          duplicateWarning,
          pendingFormId: formId,
          pendingOptions: options
        });
        return;
      }
      
      // C4 / V3-003: the saved amount differs from the one this card showed (edited in another
      // session, or re-priced at the rate on each line's date). Nothing was approved. Show both
      // amounts; *Approve New Amount* approves at the saved amount, Cancel refreshes the card.
      const amountChange = readAmountChange(error.response?.status, error.response?.data);
      if (amountChange) {
        const confirmed = await confirmAmountChanged(amountChange);
        if (confirmed) {
          await handleApprove(formId, confirmDuplicate, { ...options, displayedTotal: amountChange.serverTotal });
        } else {
          await fetchForms(false);
        }
        return;
      }

      // Extract error message from server response
      // V3-015: a 5xx never shows the server's raw text.
      const errorMessage = describeDecisionError(error, 'approve');
      failDecision(formId, errorMessage, () => void handleApprove(formId, confirmDuplicate, options));
    }
  };

  // Handle duplicate warning modal actions
  const handleDuplicateWarningConfirm = () => {
    // Close modal and proceed with approval
    const formId = duplicateWarningModal.pendingFormId;
    const pendingOptions = duplicateWarningModal.pendingOptions;
    setDuplicateWarningModal({
      isOpen: false,
      duplicateWarning: null,
      pendingFormId: null,
      pendingOptions: undefined
    });
    
    if (formId) {
      // Retry approval with confirmDuplicate = true, keeping the vendor-PDF answer.
      handleApprove(formId, true, pendingOptions);
    }
  };

  const handleDuplicateWarningCancel = () => {
    // Just close the modal without approving
    setDuplicateWarningModal({
      isOpen: false,
      duplicateWarning: null,
      pendingFormId: null
    });
  };

  const handleReject = async (formId: string, comment: string) => {
    // Mark form as processing immediately
    startDecision(formId, 'reject');

    try {
      await formService.rejectForm(formId, comment);
      
      finishDecision(formId, 'Sent for revisions – moving to Need Revisions', [], 'need revisions');
    } catch (error) {
      console.error('Error rejecting form:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      const message = describeDecisionError(error, 'send for revisions');
      failDecision(formId, message, () => void handleReject(formId, comment));
    }
  };

  const handleDecline = async (formId: string, comment: string) => {
    // Mark form as processing immediately
    startDecision(formId, 'decline');

    try {
      await formService.declineForm(formId, comment);
      
      finishDecision(formId, 'Declined – moving to Declined', [], 'declined');
    } catch (error) {
      console.error('Error declining form:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      failDecision(formId, describeDecisionError(error, 'decline'), () => void handleDecline(formId, comment));
    }
  };

  const handleWithdrawApproval = async (formId: string) => {
    // Mark form as processing immediately
    startDecision(formId, 'withdrawApproval');

    try {
      await formService.withdrawApproval(formId);
      
      finishDecision(formId, 'Approval withdrawn – back to Pending', [], 'pending');
    } catch (error) {
      console.error('Error withdrawing approval:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      const message = describeDecisionError(error, 'withdraw the approval');
      const isWithdrawBlocked = message.toLowerCase().startsWith('cannot withdraw approval');
      // A blocked withdrawal is an answer, not a failure: no Retry.
      failDecision(formId, message, isWithdrawBlocked ? undefined : () => void handleWithdrawApproval(formId));
    }
  };

  const handleWithdrawDecline = async (formId: string) => {
    startDecision(formId, 'withdrawDecline');

    try {
      await formService.withdrawDecline(formId);

      finishDecision(formId, 'Decline withdrawn – back to Pending', [], 'pending');
    } catch (error) {
      console.error('Error withdrawing decline:', error);

      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      const message = describeDecisionError(error, 'withdraw the decline');
      failDecision(formId, message, () => void handleWithdrawDecline(formId));
    }
  };

  const handleWithdrawRevisions = async (formId: string) => {
    // Mark form as processing immediately
    startDecision(formId, 'withdrawRevisions');

    try {
      await formService.withdrawRevisions(formId);
      
      finishDecision(formId, 'Revisions withdrawn – back to Pending', [], 'pending');
    } catch (error) {
      console.error('Error withdrawing revisions:', error);
      
      // Remove from processing state on error
      setProcessingForms(prev => {
        const next = new Map(prev);
        next.delete(formId);
        return next;
      });

      failDecision(formId, describeDecisionError(error, 'withdraw the revisions'), () => void handleWithdrawRevisions(formId));
    }
  };

  const handleSaveField = async (formId: string, fieldId: number, value: string) => {
    try {
      setIsLoading(true);
      await formService.updateFormField(formId, fieldId, value);
      
      // Update the local state with the updated field (functional update to avoid stale closure)
      setForms(prevForms => prevForms.map(form => {
        if (form.id === formId) {
          return {
            ...form,
            fields: form.fields.map(field => {
              if (field.id === fieldId) {
                return { ...field, value };
              }
              return field;
            })
          };
        }
        return form;
      }));
      // The saved value is already visible in the field, so no success message (STATE-06).
    } catch (error) {
      console.error('Error updating field:', error);
      failDecision(
        formId,
        describeActionError(error, "Couldn't save the change. Try again."),
        () => void handleSaveField(formId, fieldId, value)
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleFieldsReplace = (formId: string, fields: Form['fields']) => {
    setForms((prevForms) =>
      prevForms.map((form) => (form.id === formId ? { ...form, fields } : form)),
    );
  };

  // V3-018: Resubmit shows its result on the card for about 5 s (new status Pending), then the
  // card leaves – the same shared decision feedback as Approve or Decline.
  const handleHeResubmitted = async (formId: string) => {
    finishDecision(formId, 'Resubmitted – moving to Pending', [], 'pending');
  };

  const handleApplyFilters = (filters: FormAdvancedFilters) => {
    console.log('Applying filters:', filters); // Debug log
    setAdvancedFilters(filters);
    // Reset to first page when applying filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setAdvancedFilters({ ...EMPTY_ADVANCED_FILTERS });
    // Clear All returns the page to its defaults, search included (FLT-03, FLT-09).
    setSearchInput('');
    setSearchTerm('');
    // Reset to first page when clearing filters
    setPagination(prev => ({ ...prev, page: 1 }));
  };





  const waitingForServiceAreaOptions =
    advancedFilters.serviceAreaIds.length > 0 && filterOptions.serviceAreas.length === 0;

  // Any advanced (card) filter selected? All dropdown filters are multi-select, so these are lists.
  const hasActiveAdvancedFilters =
    advancedFilters.employeeNames.length > 0 ||
    advancedFilters.subcontractorNames.length > 0 ||
    advancedFilters.foremanAreaIds.length > 0 ||
    advancedFilters.serviceAreaIds.length > 0 ||
    Boolean(advancedFilters.startDate) ||
    Boolean(advancedFilters.endDate);

  // Names for the "Active Filters" badges (ids fall back to themselves if options aren't loaded yet)
  const selectedServiceAreaNames = advancedFilters.serviceAreaIds.map(
    (id) => filterOptions.serviceAreas.find((area) => area.id.toString() === id)?.name ?? id
  );
  const selectedForemanAreaNames = advancedFilters.foremanAreaIds.map(
    (id) => filterOptions.foremanAreas.find((area) => area.id.toString() === id)?.name ?? id
  );

  // V2-046: filters, status and type stay on screen; only the list area shows loading or errors.
  const listLoading = loading || waitingForServiceAreaOptions;

  return (
    <div className="tw-w-full">

      {/* When viewing a single timecard via deep link, show back link and only that form */}
      {formIdFromUrl && (
        <div className="tw-mb-3">
          <Button as={Link} to="/submissions" variant="outline-secondary" size="sm">
            <i className="bi bi-arrow-left tw-mr-2" aria-hidden />
            Back to all submissions
          </Button>
        </div>
      )}

      {/* FormFilters component - always show */}
      <FormFilters
        forms={forms}
        filterOptions={filterOptions}
        filters={advancedFilters}
        formType={formType}
        onClearFilters={handleClearFilters}
        onApplyFilters={handleApplyFilters}
        searchActive={Boolean(searchTerm)}
        search={{
          value: searchInput,
          onChange: setSearchInput,
          onSearch: handleSearch,
          placeholder: 'Ticket #, form, employee, task, asset or comments',
          label: 'Search submissions',
        }}
      />

      <div className="tw-mb-3">
        <div className="tw-flex tw-flex-col tw-gap-3 md:tw-hidden">
          <label className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">Status</span>
            <select
              className="tw-w-full tw-min-h-[44px] tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-2 tw-text-base tw-text-content"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              aria-label="Filter by status"
            >
              {STATUS_FILTER_BUTTONS.map(({ value: status, label }) => {
                const count = getFormCountByStatus(status);
                const optionLabel =
                  count > 0 && status !== 'all' && status !== 'approved' && status !== 'declined'
                    ? `${label} (${count})`
                    : label;
                return (
                  <option key={status} value={status}>{optionLabel}</option>
                );
              })}
            </select>
          </label>
          <label className="tw-flex tw-flex-col tw-gap-1">
            <span className="tw-text-xs tw-font-semibold tw-tracking-wide tw-text-content-muted">Form Type</span>
            <select
              className="tw-w-full tw-min-h-[44px] tw-rounded tw-border tw-border-line-input tw-bg-surface tw-px-3 tw-py-2 tw-text-base tw-text-content"
              value={formType}
              onChange={(e) => onFormTypeChange?.(e.target.value as ApprovalFormType)}
              aria-label="Filter by form type"
            >
              {APPROVAL_FORM_TYPES.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </label>
          {submittedByMeSwitch('submitted-by-me-mobile')}
        </div>

        <div className="tw-hidden md:tw-flex md:tw-flex-row md:tw-justify-between md:tw-items-center tw-gap-3">
          <div className="tw-flex tw-flex-wrap tw-gap-2">
            {STATUS_FILTER_BUTTONS.map(({ value: status, label }) => {
              const count = getFormCountByStatus(status);
              const active = statusFilter === status;
              const showCount =
                count > 0 && status !== 'all' && status !== 'approved' && status !== 'declined';
              return (
                <Button
                  key={status}
                  type="button"
                  variant={active ? 'primary' : 'outline-primary'}
                  aria-pressed={active}
                  onClick={() => handleStatusFilterChange(status)}
                >
                  {label}
                  {showCount && (
                    <Badge bg="warning" text="dark" className="tw-ml-2">
                      {count}
                    </Badge>
                  )}
                </Button>
              );
            })}
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {submittedByMeSwitch('submitted-by-me')}
            {APPROVAL_FORM_TYPES.map((type) => (
              <Button
                key={type.value}
                type="button"
                variant={formType === type.value ? 'primary' : 'outline-primary'}
                aria-pressed={formType === type.value}
                onClick={() => onFormTypeChange?.(type.value)}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Show filter summary if any filters are active */}
      {hasActiveAdvancedFilters && (
        <Alert variant="light" className="tw-mb-3 tw-flex tw-items-center">
          <span className="tw-mr-2">
            <i className="bi bi-funnel-fill tw-text-content-secondary"></i>
          </span>
          <div className="tw-grow">
            <strong>Active Filters:</strong>
            {advancedFilters.serviceAreaIds.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Service Area: {selectedServiceAreaNames.join(', ')}</Badge>}
            {advancedFilters.foremanAreaIds.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Foreman Area: {selectedForemanAreaNames.join(', ')}</Badge>}
            {advancedFilters.employeeNames.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Employee: {advancedFilters.employeeNames.join(', ')}</Badge>}
            {advancedFilters.subcontractorNames.length > 0 && <Badge bg="info" text="dark" className="tw-ml-2">Subcontractor: {advancedFilters.subcontractorNames.join(', ')}</Badge>}
            {advancedFilters.startDate && <Badge bg="info" text="dark" className="tw-ml-2">Work From: {advancedFilters.startDate}</Badge>}
            {advancedFilters.endDate && <Badge bg="info" text="dark" className="tw-ml-2">Work To: {advancedFilters.endDate}</Badge>}
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            className="tw-ml-auto"
            onClick={handleClearFilters}
          >
            Clear All
          </Button>
        </Alert>
      )}

      {/* Show no forms message if no forms found */}
      {listLoading ? (
        <div className="tw-flex tw-justify-center tw-p-5">
          <Spinner>Loading...</Spinner>
        </div>
      ) : error ? (
        <Alert variant="danger" className="tw-m-3" role="alert">
          {error}
        </Alert>
      ) : forms.length === 0 && missingFormId ? (
        <Alert variant="warning" role="status">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
            <span>{`Couldn't find this ${missingWhat} (${missingFormId}).`}</span>
            {missingReturnTo ? (
              <Link to={missingReturnTo}>Back to the Unit</Link>
            ) : (
              <Link to="/submissions">Back to Submissions</Link>
            )}
          </div>
        </Alert>
      ) : forms.length === 0 ? (
        <Alert variant="info">
          <div className="tw-flex tw-items-center">
            <i className="bi bi-info-circle tw-mr-2"></i>
            <div>
              <strong>No forms found.</strong>
              {(hasActiveAdvancedFilters || searchTerm) && (
                <div className="tw-mt-1">
                  Try adjusting your filters or search criteria to see more results.
                </div>
              )}
            </div>
          </div>
        </Alert>
      ) : (
        <>
          <div className="forms-list forms-container">
            {forms.map(form => (
              <FormCard
                key={form.id}
                form={form}
                // handleApprove also carries `confirmDuplicate`, which is internal to the
                // duplicate-warning retry and not the card's business.
                onApprove={(formId, options) => handleApprove(formId, false, options)}
                onReject={handleReject}
                onDecline={handleDecline}
                onSaveField={handleSaveField}
                onFieldsReplace={handleFieldsReplace}
                onHeResubmitted={handleHeResubmitted}
                onWithdrawApproval={handleWithdrawApproval}
                onWithdrawRevisions={handleWithdrawRevisions}
                onWithdrawDecline={handleWithdrawDecline}
                availableTasks={availableTasks}
                tasksLoading={tasksLoading}
                persistedLabourEfforts={persistedLabourByFormId[String(form.id)] ?? []}
                labourOvertimeAllocations={labourOtAllocationsByFormId[String(form.id)] ?? []}
                onLabourOtAllocationsRefresh={() => refreshLabourAndLightTasksForForms(forms)}
                isProcessing={processingForms.has(form.id)}
                processingAction={processingForms.get(form.id) || null}
                isFading={fadingForms.has(form.id) || decisionResults.get(form.id)?.tone === 'success'}
                decisionResult={decisionResults.get(form.id) ?? null}
                fadingAction={fadingForms.get(form.id) || null}
                defaultExpanded={expandFormId != null && String(form.id) === String(expandFormId)}
              />
            ))}
          </div>

          {/* Add pagination controls (hidden when viewing single form via formId deep link) */}
          {!formIdFromUrl && pagination.totalPages > 1 && (
            <div className="tw-flex tw-justify-center tw-mt-3 pagination-container">
              <ServerPagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalCount={pagination.totalCount}
                pageSize={pagination.limit}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                loading={loading}
                className="tw-mt-3"
              />
            </div>
          )}
        </>
      )}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
      />

      {/* Duplicate Form Warning Modal */}
      <DuplicateFormWarningModal
        isOpen={duplicateWarningModal.isOpen}
        onClose={handleDuplicateWarningCancel}
        onConfirm={handleDuplicateWarningConfirm}
        duplicateWarning={duplicateWarningModal.duplicateWarning}
      />

    </div>
  );
};

export default FormList;